import { ANALYTICS_EVENT_OBJECT } from '@/constants/analytics';
import { PROJECT_ROOT } from '@/constants/paths';
import { useWhiteLabeling } from '@/hooks/useWhiteLabeling';
import {
  EXPLORE_FILES_TAB,
  MANUAL_ANNOTATION_PROCESS_FEATURE_FLAG,
  PANORAMA_PROCESS_FEATURE_FLAG,
} from '@/utils/split';
import { inverted, Tooltip, TooltipContent, TooltipTrigger } from '@skand/ui';
import { useTreatments } from '@splitsoftware/splitio-react';
import { Link } from 'react-router-dom';
import { TabKey, useLayout } from '../stores/layout';
import { cn } from '../utils/classname';

interface ItemProps {
  name: TabKey;
  title: string;
  iconClass?: string;
  iconChild?: (props: { isActive: boolean; className?: string }) => React.ReactNode;
  isDisabled?: boolean;
  dataAnalyticsEventObject?: string;
}

export const Item = ({
  name,
  title,
  iconChild: IconChild,
  iconClass,
  isDisabled,
  dataAnalyticsEventObject,
}: ItemProps) => {
  const tab = useLayout(state => state.tab);
  const switchTab = useLayout(state => state.switch);
  const hideLeftSideBarI = useLayout(state => state.hideLeftSideBarI);
  const isShowingLeftSideBarI = useLayout(state => state.isShowingLeftSideBarI);
  const showLeftSideBarI = useLayout(state => state.showLeftSideBarI);
  const isActive = name === tab;
  const hideLeftSideBarII = useLayout(state => state.hideLeftSideBarII);

  const toggleTabs = () => {
    hideLeftSideBarII();
    if (isActive && isShowingLeftSideBarI) {
      hideLeftSideBarI();
    }
    if (!isShowingLeftSideBarI) {
      showLeftSideBarI();
    }

    switchTab(name);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'cursor-pointer px-2 py-4',
            'flex-col',
            'flex-nowrap',
            'flex',
            'items-center',
            'justify-center',
          )}
          data-analytics-event-object={dataAnalyticsEventObject}
          onClick={toggleTabs}
        >
          {IconChild ? (
            <IconChild className="h-5 w-5" isActive={isActive} />
          ) : (
            <div
              className={cn(
                'text-5 transition-colors',
                iconClass,
                isDisabled
                  ? 'color-neutral-300'
                  : isActive
                  ? 'color-primary-500'
                  : 'color-neutral-600 hover:color-neutral-800',
              )}
            />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent align="center" side="right" sideOffset={8}>
        <div className={inverted}>{title}</div>
      </TooltipContent>
    </Tooltip>
  );
};

export const LeftTabBarV2 = ({ isDisabled }: { isDisabled?: boolean }) => {
  const tabs = useLayout(state => state.tabs);
  const treatment = useTreatments([
    MANUAL_ANNOTATION_PROCESS_FEATURE_FLAG,
    PANORAMA_PROCESS_FEATURE_FLAG,
    EXPLORE_FILES_TAB,
  ]);
  const processTabFlag =
    treatment[MANUAL_ANNOTATION_PROCESS_FEATURE_FLAG].treatment === 'on' ||
    treatment[PANORAMA_PROCESS_FEATURE_FLAG].treatment === 'on';
  const filesTabFlag = treatment[EXPLORE_FILES_TAB].treatment === 'on';

  const hasBannerSetUp = useWhiteLabeling().hasWhiteLabeling;

  return (
    <div
      className={cn(
        'flex flex-col justify-between overflow-hidden mb-2 flex-none',
        hasBannerSetUp ? 'mt-2' : 'mt-11',
        isDisabled && 'opacity-50 pointer-events-none',
      )}
    >
      <div className="flex flex-col gap-1">
        {tabs.map((item, index) => {
          if (
            !item.hide &&
            !(item.key === 'process' && !processTabFlag) &&
            !(item.key === 'files' && !filesTabFlag)
          )
            return (
              <Item
                dataAnalyticsEventObject={item.dataAnalyticsEventObject}
                iconChild={item.iconChild}
                iconClass={item.iconClass}
                isDisabled={isDisabled}
                key={`item-${index}`}
                name={item.key}
                title={item.title}
              />
            );
        })}
      </div>

      <Link className="decoration-none" to={PROJECT_ROOT}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'cursor-pointer px-2 py-4 h-52px',
                'flex-col',
                'flex-nowrap',
                'flex',
                'items-center',
                'justify-center',
              )}
              data-analytics-event-object={ANALYTICS_EVENT_OBJECT.CLOSE_PROJECT}
            >
              <div
                className={cn(
                  'text-3 i-skand-close transition-colors color-neutral-600 hover:color-neutral-800',
                )}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent align="center" side="right" sideOffset={8}>
            <div className={inverted}>Close project</div>
          </TooltipContent>
        </Tooltip>
      </Link>
    </div>
  );
};
